import { ampli } from '@/ampli';
import Decimal from 'decimal.js-light';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { formatUnits, parseUnits } from 'viem';

import { tokenFormatter } from '@/lib/formatting';
import { isTokenRestrictedInCountry } from '@/lib/geo/restrictedIssuers';

import { SelectTokenDialog, type SelectTokenFilter } from '@/components/SelectTokenDialog';
import { LONG_TOKEN_SYMBOL_LENGTH } from '@/components/TokenProfile/TokenProfileHeader';
import { TokenPriceAlert } from '@/components/TransactionModule/Alerts/TokenPriceAlert';
import { AmountInputGroup } from '@/components/TransactionModule/AmountInputGroup';
import { PercentageChangeBadge } from '@/components/TransactionModule/Badges/PercentageChangeBadge';
import { InlineSuggestedTokens } from '@/components/TransactionModule/InlineSuggestedTokens';
import { ExpiryDropDownMenu } from '@/components/TransactionModule/LimitMode/ExpiryDropDownMenu';
import { LimitPriceShortcuts } from '@/components/TransactionModule/LimitMode/LimitPriceShortcuts';
import { LimitPriceSwapTokenButton } from '@/components/TransactionModule/LimitMode/LimitPriceSwapTokenButton';
import { OrderTypeDropDownMenu } from '@/components/TransactionModule/LimitMode/OrderTypeDropDownMenu';
import { PercentageInput } from '@/components/TransactionModule/LimitMode/PercentageInput';
import { TooltipLimitOrderFee } from '@/components/TransactionModule/SwapDetailsAccordion/Tooltips/Fee/TooltipLimitOrderFee';
import { SwitchTokensButton } from '@/components/TransactionModule/SwitchTokensButton';
import { useTokenBalanceValue } from '@/components/TransactionModule/TMTokenBalance';

import {
  getAdvancedOrderProvider,
  getLimitOrderProtocolAddress,
  isAdvancedOrderTypeSupported,
  isLimitOrderSupportedChainId,
  isTriggerOrderType,
} from '@/constants/advancedOrder';
import { CHAIN_IDS, LIMIT_ORDER_SUPPORTED_CHAINS } from '@/constants/chain';
import { Issuers } from '@/constants/issuers';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { useAllowance } from '@/hooks/useAllowance';
import { useGeoCheck } from '@/hooks/useGeoCheck';
import { useIsSmartWallet } from '@/hooks/useIsSmartWallet';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useTokenFeeConfig } from '@/hooks/useTokenFeeConfig';

import {
  approvalSettingsSelector,
  approveWithoutBalanceDependencies,
  useApprovalSettingsStore,
} from '@/store/approvalSettings';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import type { BadgeVariants } from '@/ui/Badge';
import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';
import { Slider } from '@/ui/Slider';

import { isTokenBlockedByTokenList } from '@/utils/0x/token-blocking';
import type { TokenBase } from '@/utils/0x/token-registry.types';
import { toAmpliSwapOrderType } from '@/utils/advancedOrderAnalytics';
import { getAdvancedOrderReceiveRecap } from '@/utils/advancedOrderFees';
import { SEARCH_TYPES } from '@/utils/amplitude';
import { classNames } from '@/utils/classnames';
import { type ChainAddress, isEthereumAddress } from '@/utils/models';
import { doesTokenNeedSellApproval, isTokenAddressNativeAsset } from '@/utils/multichain';
import { clamp, floorDecimals, isNullAmount } from '@/utils/numbers';
import { truncate } from '@/utils/string';
import { isWrap } from '@/utils/swap';

import { useModuleContext } from '../ModuleContext';
import { SelectTokenTrigger } from '../SelectToken';
import { DEFAULT_TOKEN_INPUT, type TokenInputValues } from '../store';
import type { AdvancedTradeQuote, TokenUsdPrices } from '../tradeQuote';
import { useLimitRate } from '../useLimitRate';
import { useTradeEngine } from '../useTradeEngine';
import { useTradeInputs } from '../useTradeInputs';
import { useValidation } from '../useValidation';
import { CheckoutAlerts } from './CheckoutAlerts';
import { TokenBalanceLine } from './TokenBalanceLine';
import { TokenInfoButton } from './TokenInfoButton';
import { TradeCta } from './TradeCta';

const Divider = () => <div className="relative z-1 min-h-px bg-token-gray-300 h-px w-full" />;

function SymbolWithTooltip({
  symbol,
  showPopover,
}: {
  symbol: string | undefined;
  showPopover: boolean;
}) {
  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <span>{symbol ? truncate(symbol, LONG_TOKEN_SYMBOL_LENGTH, true) : null}</span>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>{symbol}</MatchaTooltipContent>
      {showPopover && <MatchaTooltipPopover sideOffset={0} style={{ maxWidth: 'fit-content' }} />}
    </MatchaTooltip>
  );
}

type TradeDirection = 'buy' | 'sell';

/** Renders the rewrite-owned order form when the engine selects the Advanced lane. */
export function AdvancedPanel() {
  const { quote, tokenUsdPrices, hasUsdPriceResponse } = useTradeEngine();
  if (quote.source !== 'advanced') return null;

  return (
    <AdvancedPanelContent
      quote={quote}
      tokenUsdPrices={tokenUsdPrices}
      hasUsdPriceResponse={hasUsdPriceResponse}
    />
  );
}

function AdvancedPanelContent({
  quote,
  tokenUsdPrices,
  hasUsdPriceResponse,
}: {
  quote: AdvancedTradeQuote;
  tokenUsdPrices: TokenUsdPrices;
  hasUsdPriceResponse: boolean;
}) {
  const {
    variant,
    sellToken,
    buyToken,
    isSellTokenLoading,
    isBuyTokenLoading,
    onSelectToken,
    onFlipSelectedTokens,
  } = useModuleContext();
  const isLoading = isSellTokenLoading || isBuyTokenLoading;
  const chainId = sellToken?.chainId ?? buyToken?.chainId;
  const [advancedOrderType] = useAdvancedOrderType();
  const isTriggerOrder = isTriggerOrderType(advancedOrderType);
  const isOrderTypeSupported =
    chainId === undefined || isAdvancedOrderTypeSupported(chainId, advancedOrderType);
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const activeWalletAddress = (
    chainId === CHAIN_IDS.SOLANA ? solanaWallet?.address : ethereumWallet?.address
  ) as ChainAddress | undefined;
  const advancedOrderProvider =
    chainId !== undefined && isLimitOrderSupportedChainId(chainId)
      ? getAdvancedOrderProvider(chainId)
      : undefined;
  const allowanceTarget =
    advancedOrderProvider === '1inch' && chainId !== undefined
      ? getLimitOrderProtocolAddress(chainId)
      : undefined;
  const {
    sellInput,
    buyInput,
    setInputMode,
    switchInputs,
    updateSellInput,
    updateBuyInput,
    updateInputs,
  } = useTradeInputs();
  const [directionOfSwap, setDirectionOfSwap] = useState<TradeDirection>('sell');
  const [isSellAmountLocked, setIsSellAmountLocked] = useState(false);
  const {
    rate: effectiveLimitRate,
    side: limitPriceTokenSide,
    flipSide: invertLimitPriceTokenSide,
    flipTokens: flipLimitRateForTokenSwap,
    setRate: updateLimitRate,
    reset: resetLimitPrice,
    marketRate,
    limitBlockResult,
  } = useLimitRate({
    sellToken,
    buyToken,
    isLoading,
    tokenUsdPrices,
    hasUsdPriceResponse,
    autoInit: !isTriggerOrder,
    orderType: advancedOrderType,
  });
  const sellLimitRateNumber = parseFloat(effectiveLimitRate.sell);

  const updateSellInputAdapter = useCallback(
    (value?: TokenInputValues, direction?: TradeDirection) => {
      updateSellInput(value);
      if (direction) setDirectionOfSwap(direction);
    },
    [updateSellInput],
  );
  const updateBuyInputAdapter = useCallback(
    (value?: TokenInputValues, direction?: TradeDirection) => {
      updateBuyInput(value);
      if (direction) setDirectionOfSwap(direction);
    },
    [updateBuyInput],
  );
  const updateInputsAdapter = useCallback(
    (buy?: TokenInputValues, sell?: TokenInputValues, direction?: TradeDirection) => {
      updateInputs(buy, sell);
      if (direction) setDirectionOfSwap(direction);
    },
    [updateInputs],
  );

  const { data: tokenFeeConfigData } = useTokenFeeConfig(
    {
      chainIdA: chainId,
      tokenAddressA:
        sellToken && isEthereumAddress(sellToken.address) ? sellToken.address : undefined,
      tokenAddressB: buyToken && isEthereumAddress(buyToken.address) ? buyToken.address : undefined,
      chainIdB: chainId,
    },
    { enabled: Boolean(sellToken && buyToken) },
  );
  const isStableToStablePair = Boolean(tokenFeeConfigData?.isStablecoinPair);
  const isOrderingDisabled = !sellToken || !buyToken || !isOrderTypeSupported;

  const calculateAndUpdateNewLimitRate = useCallback(() => {
    const sellAmountNumber = parseFloat(sellInput.decimal) || 0;
    const buyAmountNumber = parseFloat(buyInput.decimal) || 0;
    const newRate = sellAmountNumber > 0 ? buyAmountNumber / sellAmountNumber : 0;
    updateLimitRate(String(newRate), 'sell');
  }, [buyInput.decimal, sellInput.decimal, updateLimitRate]);

  const flipSelectedBuySellTokens = useCallback(() => {
    flipLimitRateForTokenSwap();
    switchInputs();
    setDirectionOfSwap((direction) => (direction === 'buy' ? 'sell' : 'buy'));
    onFlipSelectedTokens();
  }, [flipLimitRateForTokenSwap, onFlipSelectedTokens, switchInputs]);

  const handleLimitRateChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.validity.valid) {
      updateLimitRate(event.currentTarget.value, limitPriceTokenSide);
    }
  };
  const handleSwapSellBuyTokensEvent = () => {
    ampli.tokenPairFlipped({
      buy_token_symbol: buyToken?.symbol,
      sell_token_symbol: sellToken?.symbol,
    });
    flipSelectedBuySellTokens();
  };
  const handleLimitRateChange = (multiplier: number) => {
    const parsedMarketRate =
      limitPriceTokenSide === 'sell' ? parseFloat(marketRate) : 1 / parseFloat(marketRate);
    if (!Number.isFinite(parsedMarketRate)) return;

    if (directionOfSwap === 'buy' && isSellAmountLocked) {
      switchInputs();
      setDirectionOfSwap('sell');
    }

    const computedRate = parsedMarketRate + multiplier * parsedMarketRate;
    if (!Number.isFinite(computedRate)) return;
    updateLimitRate(String(computedRate), limitPriceTokenSide);
  };

  const handleSelectToken = useCallback(
    (selectedToken: TokenBase, direction: TradeDirection) => {
      const isBuyToken = direction === 'buy';
      const otherToken = isBuyToken ? sellToken : buyToken;
      const areSameTokensSelected =
        selectedToken.address === otherToken?.address &&
        selectedToken.chainId === otherToken?.chainId;

      if (!isBuyToken || selectedToken.chainId !== chainId) {
        updateInputsAdapter(DEFAULT_TOKEN_INPUT, DEFAULT_TOKEN_INPUT, 'sell');
      } else {
        updateBuyInputAdapter(DEFAULT_TOKEN_INPUT, 'buy');
      }

      if (areSameTokensSelected) {
        flipSelectedBuySellTokens();
      } else {
        resetLimitPrice();
        onSelectToken(selectedToken, direction);
      }
    },
    [
      buyToken,
      chainId,
      flipSelectedBuySellTokens,
      onSelectToken,
      resetLimitPrice,
      sellToken,
      updateBuyInputAdapter,
      updateInputsAdapter,
    ],
  );
  const handleSelectSellToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'sell'),
    [handleSelectToken],
  );
  const handleSelectBuyToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'buy'),
    [handleSelectToken],
  );

  const calculateNewSwapInput = useCallback(
    (input: TradeDirection, otherInputDecimal?: string) => {
      const { buyTokenUsd, sellTokenUsd } = tokenUsdPrices;
      const isBuyInput = input === 'buy';
      const otherInputAmount = parseFloat(
        otherInputDecimal ?? (isBuyInput ? sellInput.decimal : buyInput.decimal),
      );
      const inputAmount =
        sellLimitRateNumber > 0
          ? otherInputAmount * (isBuyInput ? sellLimitRateNumber : 1 / sellLimitRateNumber)
          : 0;
      const amountUsd = (isBuyInput ? buyTokenUsd : sellTokenUsd) * inputAmount;
      const amountParsed = floorDecimals(inputAmount, inputAmount > 100 ? 2 : 6).toString();
      const amountFormatted = isNullAmount(otherInputAmount)
        ? ''
        : tokenFormatter.format(amountParsed, { mode: 'write' });
      const newInput = { usd: amountUsd, decimal: amountParsed, formatted: amountFormatted };
      return isNullAmount(inputAmount) ? DEFAULT_TOKEN_INPUT : newInput;
    },
    [buyInput.decimal, sellInput.decimal, sellLimitRateNumber, tokenUsdPrices],
  );

  useEffect(() => {
    if (isTriggerOrder) return;
    if (directionOfSwap !== 'sell') return;
    const newBuyInput = calculateNewSwapInput('buy');
    if (
      newBuyInput.decimal === buyInput.decimal &&
      newBuyInput.formatted === buyInput.formatted &&
      newBuyInput.usd === buyInput.usd
    ) {
      return;
    }
    updateBuyInput(newBuyInput);
  }, [buyInput, calculateNewSwapInput, directionOfSwap, isTriggerOrder, updateBuyInput]);

  useEffect(() => {
    if (isTriggerOrder) return;
    if (directionOfSwap !== 'buy') return;
    if (isSellAmountLocked) {
      calculateAndUpdateNewLimitRate();
    } else {
      updateSellInput(calculateNewSwapInput('sell'));
    }
  }, [
    calculateAndUpdateNewLimitRate,
    calculateNewSwapInput,
    directionOfSwap,
    isSellAmountLocked,
    isTriggerOrder,
    updateSellInput,
  ]);

  const sellAmount =
    sellInput.decimal !== '' && Number(sellInput.decimal) > 0 && sellToken
      ? parseUnits(sellInput.decimal, sellToken.decimals).toString()
      : undefined;
  const sellTokenBalance = useTokenBalanceValue({
    accountAddress: activeWalletAddress,
    chainId: sellToken?.chainId,
    tokenAddress: sellToken?.address,
  });
  const sellAmountPercentage =
    sellAmount && sellTokenBalance && sellTokenBalance.value > 0n
      ? clamp(
          new Decimal(sellAmount).div(sellTokenBalance.value.toString()).mul(100).toNumber(),
          0,
          100,
        )
      : 0;

  const handleSellAmountPercentageChange = useCallback(
    (values: number[]) => {
      if (!sellTokenBalance) return;
      const percentage = values[0];
      const scaledPercent = BigInt(Math.round(percentage * 100));
      const sellAmountUnits = (sellTokenBalance.value * scaledPercent) / 10000n;
      const sellAmountDecimal = formatUnits(sellAmountUnits, sellTokenBalance.decimals);
      const newSellInput: TokenInputValues = {
        decimal: sellAmountDecimal,
        formatted: tokenFormatter.format(sellAmountDecimal, { mode: 'write' }),
        usd: Number(sellAmountDecimal) * tokenUsdPrices.sellTokenUsd,
      };
      if (isTriggerOrder) {
        updateSellInputAdapter(newSellInput, 'sell');
      } else {
        const newBuyInput = calculateNewSwapInput('buy', sellAmountDecimal);
        updateInputsAdapter(newBuyInput, newSellInput, 'sell');
      }
    },
    [
      calculateNewSwapInput,
      isTriggerOrder,
      sellTokenBalance,
      tokenUsdPrices.sellTokenUsd,
      updateInputsAdapter,
      updateSellInputAdapter,
    ],
  );

  const isSmartWallet = useIsSmartWallet({ chainId });
  const { allowApproveWithoutBalance } = useApprovalSettingsStore(approvalSettingsSelector);
  const { data: allowance } = useAllowance(
    sellAmount && sellToken && isEthereumAddress(sellToken.address) ? sellToken.address : undefined,
    allowanceTarget,
    chainId,
  );
  const isSellTokenBlocked = isTokenBlockedByTokenList(sellToken);
  const isBuyTokenBlocked = isTokenBlockedByTokenList(buyToken);
  const { data: geoData } = useGeoCheck();
  const rwaBlockedTokenAddresses = useMemo(
    () =>
      [buyToken, sellToken]
        .filter((token) => token && geoData && isTokenRestrictedInCountry(token, geoData.country))
        .map((token) => token!.address),
    [buyToken, geoData, sellToken],
  );
  const isWrapOrUnwrapPair = Boolean(
    sellToken?.address &&
      buyToken?.address &&
      chainId != null &&
      isWrap(sellToken.address, buyToken.address, chainId),
  );
  const includesNativeToken = Boolean(
    isTokenAddressNativeAsset(buyToken?.address, chainId) ||
      (chainId !== CHAIN_IDS.SOLANA && isTokenAddressNativeAsset(sellToken?.address, chainId)),
  );
  const includesSuperstateToken =
    sellToken?.issuer?.name === Issuers.SUPERSTATE || buyToken?.issuer?.name === Issuers.SUPERSTATE;
  const checksApprovalBeforeBalance =
    approveWithoutBalanceDependencies(chainId === CHAIN_IDS.SOLANA, isSmartWallet) &&
    allowApproveWithoutBalance;
  const validationInput = useMemo(
    () => ({
      mode: 'advanced' as const,
      orderType: advancedOrderType,
      sellTokenAddress: sellToken?.address,
      buyTokenAddress: buyToken?.address,
      sellTokenChainId: sellToken?.chainId,
      buyTokenChainId: buyToken?.chainId,
      isSellTokenBlocked,
      isBuyTokenBlocked,
      rwaBlockedTokenAddresses,
      checksApprovalBeforeBalance,
      priceImpactPercent: quote.priceImpact?.percent,
      isSmartWallet,
      isWrapOrUnwrapPair,
      includesNativeToken,
      includesSuperstateToken,
      sellTokenDecimals: sellToken?.decimals,
      buyTokenDecimals: buyToken?.decimals,
      sellAmountUnits: sellAmount ? BigInt(sellAmount) : undefined,
      sellTokenBalanceUnits: sellTokenBalance?.value,
      allowanceUnits: allowance,
      sellTokenRequiresApproval: Boolean(doesTokenNeedSellApproval(sellToken?.address, chainId)),
      limitRate:
        isOrderTypeSupported && !limitBlockResult.blocked ? sellLimitRateNumber : undefined,
    }),
    [
      advancedOrderType,
      allowance,
      buyToken,
      chainId,
      checksApprovalBeforeBalance,
      includesNativeToken,
      includesSuperstateToken,
      isBuyTokenBlocked,
      isOrderTypeSupported,
      isSellTokenBlocked,
      isSmartWallet,
      isWrapOrUnwrapPair,
      limitBlockResult.blocked,
      quote.priceImpact?.percent,
      rwaBlockedTokenAddresses,
      sellAmount,
      sellLimitRateNumber,
      sellToken,
      sellTokenBalance?.value,
    ],
  );
  const validationResults = useValidation(validationInput);

  const estimatedBuyDecimal =
    isTriggerOrder &&
    quote.status === 'derived' &&
    quote.buyAmount !== undefined &&
    buyToken !== undefined
      ? formatUnits(BigInt(quote.buyAmount), buyToken.decimals)
      : '';
  const receiveRecap = getAdvancedOrderReceiveRecap({
    provider: advancedOrderProvider,
    isTriggerOrder,
    isStableToStablePair,
    grossDecimal: isTriggerOrder ? estimatedBuyDecimal : buyInput.decimal,
  });
  const shouldShowBlockedAlert =
    isOrderTypeSupported && limitBlockResult.blocked && Boolean(sellLimitRateNumber);
  const badgeVariant: BadgeVariants = limitBlockResult.blocked
    ? 'negative'
    : limitBlockResult.differencePercent > 0.025
      ? 'positive'
      : 'neutral';
  const hasCustomLimitRate =
    effectiveLimitRate.sell !== '' && Math.abs(limitBlockResult.differencePercent) > 1e-6;
  const shouldShowNoRateAlert =
    isOrderTypeSupported &&
    Boolean(sellToken && buyToken && hasUsdPriceResponse) &&
    marketRate === '' &&
    !isLoading;
  const isLongTokenSymbol = Boolean(
    (buyToken && buyToken.symbol.length > LONG_TOKEN_SYMBOL_LENGTH) ||
      (sellToken && sellToken.symbol.length > LONG_TOKEN_SYMBOL_LENGTH),
  );
  const [baseSymbol, quoteSymbol] =
    limitPriceTokenSide === 'sell'
      ? [sellToken?.symbol, buyToken?.symbol]
      : [buyToken?.symbol, sellToken?.symbol];
  const buttonAndAlertWrapClassName = classNames(
    'flex flex-col justify-center gap-6 p-6 pb-0 items-center tablet:items-start bottom-4 w-full z-10',
    { 'fixed tablet:static': variant === 'fullpage' },
  );

  const isSellTokenSelectable: SelectTokenFilter = useCallback((token) => {
    if (
      isTokenAddressNativeAsset(token.address, token.chainId) &&
      token.chainId !== CHAIN_IDS.SOLANA
    ) {
      return { selectable: false, reason: 'Unsupported for limit orders' };
    }
    return true;
  }, []);
  const isBuyTokenSelectable: SelectTokenFilter = useCallback((token) => {
    if (isTokenAddressNativeAsset(token.address, token.chainId)) {
      return { selectable: false, reason: 'Unsupported for limit orders' };
    }
    return true;
  }, []);

  return (
    <>
      {isTriggerOrder && (
        <>
          <Divider />
          <OrderTypeDropDownMenu />
        </>
      )}
      <Divider />
      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between mb-3 min-h-[18px]">
          <p className={fontStyles({ variant: 'LABEL_SM', class: 'mt-0' })}>Sell</p>
          {sellToken && activeWalletAddress && (
            <TokenBalanceLine
              accountAddress={activeWalletAddress}
              chainId={sellToken.chainId}
              tokenAddress={sellToken.address}
            />
          )}
        </div>
        <div className="flex justify-between">
          <div className="flex items-center">
            <SelectTokenDialog
              chainId={chainId}
              onSelect={handleSelectSellToken}
              supportedChains={LIMIT_ORDER_SUPPORTED_CHAINS}
              searchType={SEARCH_TYPES.SELL_TOKEN}
              filterToken={isSellTokenSelectable}
            >
              <SelectTokenTrigger activeToken={sellToken} searchType={SEARCH_TYPES.SELL_TOKEN} />
            </SelectTokenDialog>
            <TokenInfoButton direction="sell" />
          </div>
          {!sellToken?.address && chainId !== undefined && buyToken && (
            <InlineSuggestedTokens
              accountAddress={activeWalletAddress}
              chainId={chainId}
              counterpartTokenAddress={buyToken.address}
              orderType={toAmpliSwapOrderType('advanced', advancedOrderType)}
              onSelect={handleSelectSellToken}
            />
          )}
        </div>
        <AmountInputGroup
          buyInput={buyInput}
          direction="sell"
          disabled={!isOrderTypeSupported}
          sellInput={sellInput}
          tokenUsdPrices={tokenUsdPrices}
          isSellAmountLocked={isSellAmountLocked}
          inputVisualStyle={isSellAmountLocked ? 'locked' : 'default'}
          updateBuyInput={updateBuyInputAdapter}
          updateSellInput={updateSellInputAdapter}
          updateInputs={updateInputsAdapter}
          setIsSellAmountLocked={setIsSellAmountLocked}
          setInputMode={setInputMode}
        />
        <div className="flex gap-2 mt-2 items-center">
          <Slider
            disabled={!isOrderTypeSupported || !sellTokenBalance || sellTokenBalance.value === 0n}
            value={[sellAmountPercentage]}
            onValueChange={handleSellAmountPercentageChange}
            min={0}
            max={100}
          />
          <PercentageInput
            value={sellAmountPercentage}
            onChange={(value) => handleSellAmountPercentageChange([value])}
            disabled={!isOrderTypeSupported || !sellTokenBalance || sellTokenBalance.value === 0n}
            ariaLabel="Sell amount percentage"
          />
        </div>
      </div>

      <div className="flex items-center">
        <Divider />
        <SwitchTokensButton
          disabled={isLoading}
          isLoading={isLoading}
          direction={directionOfSwap === 'buy' ? 'normal' : 'reverse'}
          onClick={handleSwapSellBuyTokensEvent}
        />
        <Divider />
      </div>

      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between mb-3 min-h-[18px]">
          <p className={fontStyles({ variant: 'LABEL_SM', class: 'mt-0' })}>Buy</p>
          {buyToken && activeWalletAddress && (
            <TokenBalanceLine
              accountAddress={activeWalletAddress}
              chainId={buyToken.chainId}
              tokenAddress={buyToken.address}
            />
          )}
        </div>
        <div className="flex justify-between">
          <div className="flex items-center">
            <SelectTokenDialog
              chainId={chainId}
              onSelect={handleSelectBuyToken}
              supportedChains={LIMIT_ORDER_SUPPORTED_CHAINS}
              searchType={SEARCH_TYPES.BUY_TOKEN}
              filterToken={isBuyTokenSelectable}
            >
              <SelectTokenTrigger activeToken={buyToken} searchType={SEARCH_TYPES.BUY_TOKEN} />
            </SelectTokenDialog>
            <TokenInfoButton direction="buy" />
          </div>
          {!buyToken && chainId !== undefined && sellToken && (
            <InlineSuggestedTokens
              accountAddress={activeWalletAddress}
              chainId={chainId}
              counterpartTokenAddress={sellToken.address}
              orderType={toAmpliSwapOrderType('advanced', advancedOrderType)}
              onSelect={handleSelectBuyToken}
            />
          )}
        </div>
        {isTriggerOrder ? (
          <div className="mt-3">
            <p className={fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-800' })}>
              {receiveRecap.label}
            </p>
            <p className={fontStyles({ variant: 'H3', class: 'mt-1' })}>
              {receiveRecap.amount && buyToken
                ? tokenFormatter.format(receiveRecap.amount, {
                    symbol: buyToken.symbol,
                    mode: 'read',
                  })
                : '0'}
            </p>
          </div>
        ) : (
          <AmountInputGroup
            buyInput={buyInput}
            direction="buy"
            sellInput={sellInput}
            tokenUsdPrices={tokenUsdPrices}
            isSellAmountLocked={isSellAmountLocked}
            updateBuyInput={updateBuyInputAdapter}
            updateSellInput={updateSellInputAdapter}
            updateInputs={updateInputsAdapter}
            setInputMode={setInputMode}
          />
        )}
      </div>

      <Divider />
      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between mb-3">
          <div className="flex gap-2 items-center">
            <p className={fontStyles({ variant: 'LABEL_SM', class: 'mt-0' })}>
              When 1 <SymbolWithTooltip symbol={baseSymbol} showPopover={isLongTokenSymbol} /> is
              worth
            </p>
            <PercentageChangeBadge
              variant={badgeVariant}
              percentageChange={limitBlockResult.differencePercent}
            />
          </div>
          {hasCustomLimitRate && (
            <button
              type="button"
              className={buttons({ size: 'small', color: 'gray', shape: 'pill' })}
              onClick={resetLimitPrice}
              disabled={isOrderingDisabled}
            >
              Reset
            </button>
          )}
        </div>
        <div className="relative flex justify-between items-center gap-3">
          <input
            title={
              isOrderingDisabled
                ? 'Select supported tokens before setting a rate'
                : isTriggerOrder
                  ? 'Trigger rate'
                  : 'Limit order rate'
            }
            id="limitOrderLabelInput"
            disabled={isOrderingDisabled}
            className={fontStyles({
              variant: 'H3',
              class: [
                'w-full pr-4 border-none h-9 max-w-[75%] overflow-hidden py-0 overflow-ellipsis whitespace-nowrap bg-transparent hover:outline-none focus:outline-none placeholder:text-token-gray-600 disabled:bg-transparent disabled:cursor-not-allowed',
                limitBlockResult.blocked ? 'text-token-red-1000' : undefined,
              ],
            })}
            type="text"
            spellCheck="false"
            autoComplete="off"
            autoCorrect="off"
            pattern="^[0-9]*[.]?[0-9]*$"
            value={effectiveLimitRate[limitPriceTokenSide]}
            placeholder="0.0"
            onChange={handleLimitRateChangeEvent}
          />
          <div className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-1600' })}>
            <SymbolWithTooltip
              symbol={isOrderingDisabled ? undefined : quoteSymbol}
              showPopover={isLongTokenSymbol}
            />
          </div>
          <LimitPriceSwapTokenButton
            disabled={isOrderingDisabled}
            inverted={limitPriceTokenSide === 'buy'}
            tokenSymbol={quoteSymbol ?? 'TBD'}
            onClick={invertLimitPriceTokenSide}
          />
        </div>
        <LimitPriceShortcuts
          limitPriceTokenSide={limitPriceTokenSide}
          disabled={isOrderingDisabled}
          onChange={handleLimitRateChange}
          bias={
            isTriggerOrder
              ? advancedOrderType === 'stop-loss'
                ? limitPriceTokenSide === 'sell'
                  ? 'below'
                  : 'above'
                : limitPriceTokenSide === 'sell'
                  ? 'above'
                  : 'below'
              : undefined
          }
        />
      </div>
      {shouldShowNoRateAlert && (
        <div
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-red-1300 bg-token-red-300 p-4 rounded-xl mx-6 mb-4',
          })}
        >
          Price Unavailable, we couldn&apos;t retrieve an accurate price for this token. Please
          manually enter a limit price to proceed with your order.
        </div>
      )}
      <Divider />
      <ExpiryDropDownMenu />
      <Divider />
      {!isTriggerOrder && buyToken && buyInput.decimal !== '' && (
        <>
          <div className="flex flex-col justify-center py-4 px-6">
            <div className="flex justify-between items-center">
              <div className="flex gap-1.5 items-center">
                <p className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-800' })}>
                  {receiveRecap.label}
                </p>
                <MatchaTooltip delayDuration={0}>
                  <MatchaTooltipTrigger>
                    <span>
                      <InfoCircleSolidSmallIcon className="text-token-gray-700 mt-0.5" />
                    </span>
                  </MatchaTooltipTrigger>
                  <MatchaTooltipContent>
                    {chainId ? (
                      <TooltipLimitOrderFee
                        chainId={chainId}
                        isStableToStablePair={isStableToStablePair}
                        provider={advancedOrderProvider}
                      />
                    ) : null}
                  </MatchaTooltipContent>
                  <MatchaTooltipPopover className="py-3" />
                  <MatchaTooltipBottomSheet header="Network fee" />
                </MatchaTooltip>
              </div>
              <div className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>
                {tokenFormatter.format(receiveRecap.amount, {
                  symbol: buyToken.symbol,
                  mode: 'read',
                })}
              </div>
            </div>
          </div>
          <Divider />
        </>
      )}
      <div className={buttonAndAlertWrapClassName}>
        <TradeCta
          validationResults={validationResults}
          quote={quote}
          allowanceTarget={allowanceTarget}
        />
      </div>
      {shouldShowBlockedAlert && (
        <div className="px-6 mt-6">
          <TokenPriceAlert
            mode={isTriggerOrder ? advancedOrderType : limitPriceTokenSide}
            difference={limitBlockResult.differencePercent}
          />
        </div>
      )}
      <CheckoutAlerts results={validationResults} quote={quote} />
    </>
  );
}
